import { useMemo } from "react";

import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import {
    PermissionedLoan,
    isPermissionedRwaToken,
    useFetchPermissionedLoansQuery,
    useLoopscaleWalletContext
} from "@bridgesplit/abf-react";

export interface PermissionCheckResult {
    isPermissionedToken: boolean;
    hasPermission: boolean;
    isLoading: boolean;
    permissionDenied: boolean;
    permissionMessage: JSX.Element | null;
    permissionedLoan: PermissionedLoan | undefined;
    loanNonce: number | undefined;
}

/**
 * Hook to check if a user has permission to interact with permissioned RWA tokens
 * Returns permission status and a user-friendly message if denied
 */
export function usePermissionedLoanCheck(
    principalToken: TokenListMetadata | undefined,
    collateralToken: TokenListMetadata | undefined
): PermissionCheckResult {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const isCollateralPermissioned = useMemo(() => isPermissionedRwaToken(collateralToken), [collateralToken]);

    // Check if either token is a permissioned RWA token
    const isPrincipalPermissioned = useMemo(() => isPermissionedRwaToken(principalToken), [principalToken]);

    const isPermissionedToken = isPrincipalPermissioned || isCollateralPermissioned;

    // Only fetch permissions if we have a permissioned token and all required data
    const shouldFetch = isPermissionedToken && !!activeWallet && !!principalToken?.mint && !!collateralToken?.mint;

    const {
        data: permissionedLoans,
        isError,
        isLoading
    } = useFetchPermissionedLoansQuery(
        {
            borrower: activeWallet ?? "",
            principal: principalToken?.mint ?? "",
            collateral: collateralToken?.mint ?? ""
        },
        {
            skip: !shouldFetch
        }
    );

    const permissionedLoan = permissionedLoans?.[0];

    // Determine if user has permission
    const hasPermission = useMemo(() => {
        if (!isPermissionedToken) return true; // Non-permissioned tokens are always allowed
        if (!shouldFetch) return false; // Can't determine permission without required data
        // User has permission if we successfully fetched a permissioned loan entry
        return !isError && !!permissionedLoan;
    }, [isPermissionedToken, shouldFetch, isError, permissionedLoan]);

    const loanNonce = permissionedLoan?.nonce;
    const permissionDenied = isPermissionedToken && !hasPermission && !isLoading;

    const permissionMessage = useMemo(() => {
        if (!permissionDenied) return null;

        // User doesn't hold the token
        return (
            <>
                This is a permissioned market. Reach out <a href={"mailto:prime@loopscale.com"}>here</a> to request
                access to borrow against this asset.
            </>
        );
    }, [permissionDenied]);

    return {
        isPermissionedToken,
        hasPermission,
        isLoading,
        permissionDenied,
        permissionMessage,
        permissionedLoan,
        loanNonce
    };
}
